import React from 'react'
import PropTypes from 'prop-types'
import { useMediaQuery } from '@deseretbook/react-utils'
import { Box, Button, useTheme, Placeholder, Text, Link } from '@deseretbook/react-ui'
import PlusCircle from '@deseretbook/icons/react/plus-circle'
import InfoCircle from '@deseretbook/icons/react/info'
import { useActionContext } from '../useActionContext'

/**
 * Lists available options for the product.
 * Everything from watch, view trailer, and purchase options
 *
 * @returns {function} React Component
 */
export const Options = ({
  user = {},
  trailer = {},
  video = {},
  activatePlayer,
  physicalVariants = [],
  isDeseretVideoSubscriber = false,
  isVipSubscriber = false,
  isFree = false,
  isDark = false,
}) => {
  const [hasDeseretVideo, setHasDeseretVideo] = React.useState(isDeseretVideoSubscriber)
  const [hasVip, setHasVip] = React.useState(isVipSubscriber)
  const theme = useTheme()

  const {
    isLoading,
    infoText,
    showInfoIcon,
    helpText,
    helpTextLink,
    userVideoSub,
  } = useActionContext({
    user,
    hasDeseretVideo,
    hasVip,
    isFree,
  })

  /**
   * Refreshes the page with one of the physical variants
   *
   * @returns {string} link to physical variant
   */
  const getPhysicalVariantLink = () => {
    const variantIdWithOptions = physicalVariants[0] && physicalVariants[0].idWithOptions

    // Builds URL linking to current product with the variant id
    const url = `${location.pathname}${
      variantIdWithOptions ? `?variant_id=${variantIdWithOptions}` : null
    }`

    return url
  }

  // For when the content reaches its tablet breakpoint
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 767 })

  return (
    <React.Fragment>
      {isLoading ? (
        <Placeholder>
          <Box width={15}>
            <Placeholder.Text lines={1} />
          </Box>
          <Placeholder.Block width={10} height={2.5} marginBottom={1} />
          <Box width={7}>
            <Placeholder.Text lines={1} />
          </Box>
        </Placeholder>
      ) : (
        <React.Fragment>
          <Box justifyContent="center" isColumn>
            <Box marginBottom={0.25} alignItems="center">
              {showInfoIcon && (
                <InfoCircle
                  width="1rem"
                  height="1rem"
                  color={theme.palette.light.dark}
                  style={{ marginRight: '0.5rem' }}
                />
              )}
              <Text color={isDark ? 'light' : 'secondary'} size="small">
                {infoText}
              </Text>
            </Box>
            <Box alignItems="center" isWrapped>
              <Box
                marginRight={isTabletOrSmaller ? 0 : 1}
                marginVertical={0.5}
                isFullwidth={isTabletOrSmaller}
              >
                {isFree || hasDeseretVideo || hasVip ? (
                  <Button
                    isFullwidth={isTabletOrSmaller}
                    type="button"
                    icon="play"
                    color={hasVip ? 'vip' : 'video'}
                    onClick={() => {
                      activatePlayer(video.url)
                    }}
                  >
                    Watch Now
                  </Button>
                ) : (
                  <Button
                    isFullwidth={isTabletOrSmaller}
                    type="button"
                    color="video"
                    as="a"
                    href="/video"
                  >
                    {userVideoSub && !hasDeseretVideo && !hasVip
                      ? 'Join Deseret Video+'
                      : 'Start a Free Trial'}
                  </Button>
                )}
              </Box>
              {trailer && trailer.url && (
                <Box
                  marginRight={isTabletOrSmaller ? 0 : 1}
                  marginVertical={0.25}
                  isFullwidth={isTabletOrSmaller}
                >
                  <Button
                    isFullwidth={isTabletOrSmaller}
                    type="button"
                    icon="play"
                    onClick={() => {
                      activatePlayer(trailer.url)
                    }}
                  >
                    Watch Trailer
                  </Button>
                </Box>
              )}
              {/*  PURCHASE OPTIONS */}
              {physicalVariants.length && (
                <Box marginVertical={0.5} isFullwidth={isTabletOrSmaller}>
                  <Button isFullwidth type="button" as="a" href={getPhysicalVariantLink()}>
                    More Purchase Options
                  </Button>
                </Box>
              )}
              <Box marginLeft={1} marginVertical={0.5} alignItems="center">
                <PlusCircle width="2.5rem" height="2.5rem" color={theme.palette.light.dark} />
              </Box>
            </Box>
            <Box marginTop={0.25}>
              {helpTextLink ? (
                <Link href={helpTextLink} style={{ color: isDark ? 'white' : 'black' }}>
                  <Text color={isDark ? 'light' : 'secondary'} size="tiny">
                    {helpText}
                  </Text>
                </Link>
              ) : (
                <Text color={isDark ? 'light' : 'secondary'} size="tiny">
                  {helpText}
                </Text>
              )}
            </Box>
          </Box>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

Options.propTypes = {
  trailer: PropTypes.shape({
    url: PropTypes.string,
  }),
  video: PropTypes.shape({
    url: PropTypes.string,
  }),
  activatePlayer: PropTypes.func,
  user: PropTypes.shape(PropTypes.any),
  physicalVariants: PropTypes.arrayOf(PropTypes.object),
  isDeseretVideoSubscriber: PropTypes.bool,
  isFree: PropTypes.bool,
  isDark: PropTypes.bool,
}
